// apps/web/src/lib/actor-identity.ts
//
// LAT-701 → LAT-1164 — one resolution rule for "who acted". Audit events carry
// `actor_agent` plus, on newer rows, `actor_kind`. The kind disambiguates
// platform operators and first-party automation from generic clients.

import {
  AGENT_REGISTRY,
  type AgentCategory,
  type AgentRegistryEntry,
} from "./agent-registry";
import type { BrandGlyphKey } from "@/lib/brand-keys";

export type AuditActorKind = string;
export type FirstPartyActorSubtype =
  | "routines"
  | "source-sync"
  | "maintenance"
  | "lifecycle"
  | "webhook";

export type ActorIdentity =
  | { kind: "human" }
  | { kind: "web-app"; label: string }
  | { kind: "operator"; label: string }
  | { kind: "first-party"; subtype: FirstPartyActorSubtype; label: string }
  | { kind: "client"; category: AgentCategory; label: string; brand?: BrandGlyphKey }
  | { kind: "agent-generic"; category: AgentCategory; label: string }
  | { kind: "anonymous"; label: string }
  | { kind: "unknown"; label: string };

const FIRST_PARTY: Record<string, { subtype: FirstPartyActorSubtype; label: string }> = {
  "atmina-routines": { subtype: "routines", label: "Memory Routines" },
  "atmina-source-sync": { subtype: "source-sync", label: "Source sync" },
  "support-sweep": { subtype: "maintenance", label: "Support sweep" },
  "waitlist-reconcile": { subtype: "maintenance", label: "Waitlist reconcile" },
  "linear-webhook": { subtype: "webhook", label: "Linear webhook" },
  // LAT-1229 follow-up — the connector-management job (not the sync runner,
  // which is atmina-source-sync); a management sweep, hence "maintenance".
  "connectors-manager": { subtype: "maintenance", label: "Connectors manager" },
};

/** Drop a trailing " (…)" suffix — e.g. "openai-mcp (Codex)" → "openai-mcp". */
function stripParenthetical(value: string): string {
  return value.replace(/\s*\([^)]*\)\s*$/, "").trim();
}

/**
 * Resolve an MCP client's self-reported wire-name to a registry entry.
 * Order (LAT-1229 follow-up): exact agentKey → exact alias → normalized
 * (lowercase + trailing-parenthetical stripped) retried against lowercased
 * keys AND aliases. Returns undefined for genuinely unknown clients.
 */
function findRegistryEntry(actorAgent: string): AgentRegistryEntry | undefined {
  const exact =
    AGENT_REGISTRY.find((a) => a.agentKey === actorAgent) ??
    AGENT_REGISTRY.find((a) => a.aliases?.includes(actorAgent));
  if (exact) return exact;

  const normalized = stripParenthetical(actorAgent.toLowerCase());
  if (!normalized) return undefined;
  return AGENT_REGISTRY.find(
    (a) =>
      a.agentKey.toLowerCase() === normalized ||
      a.aliases?.some((alias) => alias.toLowerCase() === normalized),
  );
}

/** A legible fallback label for an unknown client: titleize the hyphenated
 *  wire string ("some-new-harness" → "Some New Harness") — never render raw. */
function titleizeAgentName(raw: string): string {
  return (
    raw
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") || raw
  );
}

export function resolveActorIdentity(
  actorAgent: string | null | undefined,
  actorKind?: AuditActorKind | null,
): ActorIdentity {
  if (actorKind === "operator" || actorAgent === "platform-admin") {
    return { kind: "operator", label: "Platform operator" };
  }
  if (!actorAgent) {
    // LAT-1386 — file_versions rows stamp actor_agent NULL by design (the
    // client wire-name lives on the audit plane, not the Session), so the
    // kind alone must resolve; anything else without a kind is unknown.
    if (actorKind === "anonymous") {
      return { kind: "anonymous", label: "Anonymous visitor" };
    }
    if (actorKind === "system") {
      return { kind: "first-party", subtype: "lifecycle", label: "Atmina system" };
    }
    if (actorKind === "user") {
      return { kind: "human" };
    }
    return { kind: "unknown", label: "Unknown client" };
  }
  if (actorAgent === "atmina-web" || actorAgent === "dashboard") {
    return { kind: "web-app", label: "Atmina Web app" };
  }
  if (actorAgent === "unknown-anon") {
    return { kind: "anonymous", label: "Anonymous visitor" };
  }
  const firstParty = FIRST_PARTY[actorAgent];
  if (firstParty) {
    return { kind: "first-party", ...firstParty };
  }
  if (actorAgent === "system" || (actorAgent === "unknown" && actorKind === "system")) {
    return { kind: "first-party", subtype: "lifecycle", label: "Atmina system" };
  }
  if (actorAgent === "unknown") {
    return { kind: "unknown", label: "Unknown client" };
  }
  const entry = findRegistryEntry(actorAgent);
  if (entry) {
    return {
      kind: "client",
      category: entry.category,
      label: entry.displayName,
      brand: entry.brand,
    };
  }
  return {
    kind: "agent-generic",
    category: "generic",
    label: titleizeAgentName(actorAgent),
  };
}
